/**
 * Fair Style Space — Wardrobe (Unit 2) on `GET /playground/user-wardrobe`.
 * Visual canon: Figma `42:5575`.
 */

import { OUTFIT_TYPE_ID } from '@/features/capsules/api/capsules.data'
import type {
  AvailableItem,
  CapsuleInventoryItem,
  OutfitItem,
  WardrobeListItem,
  WardrobeTypeCount,
} from '@/features/style-space/api'

export const FAIR_STYLE_SPACE_WARDROBE_CATEGORY_IDS = [
  'all',
  'tops',
  'bottoms',
  'outerwear',
  'shoes',
  'bags',
  'accessories',
] as const

export type FairStyleSpaceWardrobeCategoryId =
  (typeof FAIR_STYLE_SPACE_WARDROBE_CATEGORY_IDS)[number]

/** Item type keys (exclude `all`). */
export type FairStyleSpaceWardrobeItemCategory = Exclude<
  FairStyleSpaceWardrobeCategoryId,
  'all'
>

export type FairStyleSpaceItemSource = 'wardrobe' | 'catalog'

/**
 * The three accessory slots of the canvas are not interchangeable (Figma `Playground`:
 * hat · glasses · belt). `other` (scarf, gloves, jewelry…) takes whichever is free.
 */
export type FairStyleSpaceAccessoryKind = 'hat' | 'eyewear' | 'belt' | 'other'

/** ML `main_category.id` of the wearable-accessory families (`outfit_composition.py`). */
const ACCESSORY_MAIN_CATEGORY_KIND: Readonly<Record<number, FairStyleSpaceAccessoryKind>> = {
  25: 'hat',
  63: 'eyewear',
  27: 'belt',
}

const HAT_NAME_PATTERN = /\b(hat|cap|beanie|beret|fedora|bucket)\b|шляп|кепк|шапк|берет|панам/i
const EYEWEAR_NAME_PATTERN = /glasses|sunglasses|eyewear|shades|очки/i
const BELT_NAME_PATTERN = /\bbelt\b|ремень|ремн/i

/** Which accessory slot family a piece belongs to — by the ML family, else by its name. */
export function fairStyleSpaceAccessoryKind(hints: {
  mainCategoryId?: number | null
  name?: string | null
}): FairStyleSpaceAccessoryKind {
  const byFamily = hints.mainCategoryId != null ? ACCESSORY_MAIN_CATEGORY_KIND[hints.mainCategoryId] : undefined
  if (byFamily) return byFamily
  const name = hints.name ?? ''
  if (HAT_NAME_PATTERN.test(name)) return 'hat'
  if (EYEWEAR_NAME_PATTERN.test(name)) return 'eyewear'
  if (BELT_NAME_PATTERN.test(name)) return 'belt'
  return 'other'
}

/**
 * One piece the playground can place: a wardrobe row (`w-<overview_id>`) or a
 * catalogue product (`p-<product_id>`). The two ids never collide because the
 * prefix carries which table the number points at.
 */
export interface FairStyleSpaceWardrobeItem {
  id: string
  name: string
  category: FairStyleSpaceWardrobeItemCategory
  imageSrc: string
  /** Capsule “to buy” badge — Figma ItemCard showToBuyLabel */
  toBuy?: boolean
  source: FairStyleSpaceItemSource
  /** `wardrobe_product_overview.id` for a wardrobe piece. */
  overviewId?: number
  /** Catalogue product (linked or the piece itself). */
  productId?: number | null
  variantId?: number | null
  /** ML `type.id`, when the API said. */
  typeId?: number
  brand?: string | null
  /** For `category: 'accessories'` — which of the three accessory slots it belongs in. */
  accessoryKind?: FairStyleSpaceAccessoryKind
}

export interface FairStyleSpaceWardrobeCategoryChip {
  id: FairStyleSpaceWardrobeCategoryId
  labelKey: string
  count: number
}

export const FAIR_STYLE_SPACE_WARDROBE_DEFAULT_CATEGORY: FairStyleSpaceWardrobeCategoryId =
  'all'

/** Figma category order + i18n key suffix. */
export const FAIR_STYLE_SPACE_WARDROBE_CATEGORY_DEFS: ReadonlyArray<{
  id: FairStyleSpaceWardrobeCategoryId
  labelKey: string
}> = [
  { id: 'all', labelKey: 'all' },
  { id: 'tops', labelKey: 'tops' },
  { id: 'bottoms', labelKey: 'bottoms' },
  { id: 'outerwear', labelKey: 'outerwear' },
  { id: 'shoes', labelKey: 'shoes' },
  { id: 'bags', labelKey: 'bags' },
  { id: 'accessories', labelKey: 'accessories' },
]

/* ——— ids ——— */

export function fairStyleSpaceWardrobeId(overviewId: number): string {
  return `w-${overviewId}`
}

export function fairStyleSpaceCatalogId(productId: number): string {
  return `p-${productId}`
}

export type FairStyleSpaceParsedId =
  | { kind: 'wardrobe'; overviewId: number }
  | { kind: 'catalog'; productId: number }

export function parseFairStyleSpaceItemId(id: string): FairStyleSpaceParsedId | null {
  const match = /^([wp])-(\d+)$/.exec(id)
  if (!match) return null
  const value = Number(match[2])
  return match[1] === 'w'
    ? { kind: 'wardrobe', overviewId: value }
    : { kind: 'catalog', productId: value }
}

/* ——— type → category ——— */

/** Subcategory names that mean a bag when the list gives no `main_category_id`. */
const BAG_NAME_PATTERN = /bag|tote|clutch|backpack|satchel|wallet|purse|pouch|briefcase|handbag/i

/** Wardrobe chip for an ML type; accessories split into bags by name/family. */
export function fairStyleSpaceCategoryForType(
  typeId: number | null | undefined,
  hints: { name?: string | null; isBag?: boolean } = {},
): FairStyleSpaceWardrobeItemCategory {
  switch (typeId) {
    case OUTFIT_TYPE_ID.top:
    case OUTFIT_TYPE_ID.fullbody:
      return 'tops'
    case OUTFIT_TYPE_ID.bottom:
      return 'bottoms'
    case OUTFIT_TYPE_ID.shoes:
      return 'shoes'
    case OUTFIT_TYPE_ID.outwear:
      return 'outerwear'
    case OUTFIT_TYPE_ID.accessories:
      if (hints.isBag) return 'bags'
      if (hints.name && BAG_NAME_PATTERN.test(hints.name)) return 'bags'
      return 'accessories'
    default:
      return 'accessories'
  }
}

/** Reverse: the ML `type` filter a wardrobe chip stands for (bags share accessories). */
export function fairStyleSpaceTypeForCategory(
  category: FairStyleSpaceWardrobeItemCategory,
): number {
  switch (category) {
    case 'tops':
      return OUTFIT_TYPE_ID.top
    case 'bottoms':
      return OUTFIT_TYPE_ID.bottom
    case 'shoes':
      return OUTFIT_TYPE_ID.shoes
    case 'outerwear':
      return OUTFIT_TYPE_ID.outwear
    default:
      return OUTFIT_TYPE_ID.accessories
  }
}

/* ——— mappers ——— */

export function mapWardrobeListItem(item: WardrobeListItem): FairStyleSpaceWardrobeItem {
  const category = fairStyleSpaceCategoryForType(item.type, { name: item.name })
  return {
    id: fairStyleSpaceWardrobeId(item.productOverviewId),
    name: item.name ?? '',
    category,
    imageSrc: item.image ?? '',
    source: 'wardrobe',
    overviewId: item.productOverviewId,
    typeId: item.type,
    brand: item.brand,
    // the list carries no main category — the subcategory NAME tells hat from belt
    ...(category === 'accessories' ? { accessoryKind: fairStyleSpaceAccessoryKind({ name: item.name }) } : {}),
  }
}

export function mapWardrobeListItems(
  items: readonly WardrobeListItem[] | null | undefined,
): FairStyleSpaceWardrobeItem[] {
  return (items ?? []).map(mapWardrobeListItem)
}

/** An outfit/capsule piece as a placeable item; catalogue pieces are "to buy". */
export function mapOutfitItemToWardrobeItem(
  item: Pick<OutfitItem, 'productId' | 'wardrobeProductOverviewId' | 'image' | 'typeId'> & {
    mainCategoryId?: number | null
    variantId?: number | null
    name?: string | null
    brand?: string | null
  },
  isBag = false,
): FairStyleSpaceWardrobeItem {
  const category = fairStyleSpaceCategoryForType(item.typeId, { isBag, name: item.name })
  const accessoryKind =
    category === 'accessories'
      ? fairStyleSpaceAccessoryKind({ mainCategoryId: item.mainCategoryId, name: item.name })
      : undefined
  if (item.wardrobeProductOverviewId != null) {
    return {
      id: fairStyleSpaceWardrobeId(item.wardrobeProductOverviewId),
      name: item.name ?? '',
      brand: item.brand ?? null,
      category,
      imageSrc: item.image ?? '',
      source: 'wardrobe',
      overviewId: item.wardrobeProductOverviewId,
      typeId: item.typeId,
      ...(accessoryKind ? { accessoryKind } : {}),
    }
  }
  return {
    id: fairStyleSpaceCatalogId(item.productId ?? 0),
    name: item.name ?? '',
    brand: item.brand ?? null,
    variantId: item.variantId ?? null,
    category,
    imageSrc: item.image ?? '',
    toBuy: true,
    source: 'catalog',
    productId: item.productId,
    typeId: item.typeId,
    ...(accessoryKind ? { accessoryKind } : {}),
  }
}

/** Palette row of `GET /playground/outfits/{id}/available-items` (§2.4b). */
export function mapAvailableItemToWardrobeItem(item: AvailableItem): FairStyleSpaceWardrobeItem {
  return mapOutfitItemToWardrobeItem({
    productId: item.source === 'catalog' ? item.catalogueProductId : null,
    wardrobeProductOverviewId: item.source === 'wardrobe' ? item.wardrobeProductId : null,
    image: item.imageUrl,
    typeId: item.typeId,
    variantId: item.source === 'catalog' ? item.catalogueVariantId : null,
  })
}

/** Capsule inventory row (§2.2b) — no type on the wire, category comes from the caller. */
export function mapCapsuleInventoryItemToWardrobeItem(
  item: CapsuleInventoryItem,
  category: FairStyleSpaceWardrobeItemCategory = 'accessories',
): FairStyleSpaceWardrobeItem & { capsuleItemId: number } {
  const base =
    item.source === 'wardrobe' && item.wardrobeProductId != null
      ? {
          id: fairStyleSpaceWardrobeId(item.wardrobeProductId),
          source: 'wardrobe' as const,
          overviewId: item.wardrobeProductId,
          productId: item.catalogueProductId,
          variantId: item.catalogueVariantId,
        }
      : {
          id: fairStyleSpaceCatalogId(item.catalogueProductId ?? 0),
          source: 'catalog' as const,
          productId: item.catalogueProductId,
          variantId: item.catalogueVariantId,
          toBuy: true,
        }
  return {
    ...base,
    name: '',
    category,
    imageSrc: item.imageUrl ?? '',
    capsuleItemId: item.capsuleItemId,
  }
}

/* ——— counting / filtering ——— */

export function countFairStyleSpaceWardrobeItems(
  items: readonly FairStyleSpaceWardrobeItem[],
  category: FairStyleSpaceWardrobeCategoryId = 'all',
): number {
  if (category === 'all') return items.length
  return items.filter((item) => item.category === category).length
}

export function filterFairStyleSpaceWardrobeItems(
  items: readonly FairStyleSpaceWardrobeItem[],
  category: FairStyleSpaceWardrobeCategoryId,
): FairStyleSpaceWardrobeItem[] {
  if (category === 'all') return [...items]
  return items.filter((item) => item.category === category)
}

/**
 * Category chips. `counts_by_type` (B15) gives the type totals over the WHOLE
 * wardrobe; bags vs accessories share a type, so that split (and any page we
 * did not load) is counted from the items in hand.
 */
export function getFairStyleSpaceWardrobeCategoryChips(
  items: readonly FairStyleSpaceWardrobeItem[],
  countsByType: readonly WardrobeTypeCount[] = [],
  totalItems?: number,
): FairStyleSpaceWardrobeCategoryChip[] {
  const byType = new Map(countsByType.map((entry) => [entry.type, entry.count]))
  const typeTotal = (...types: number[]) =>
    types.reduce((sum, type) => sum + (byType.get(type) ?? 0), 0)
  const hasServerCounts = countsByType.length > 0
  return FAIR_STYLE_SPACE_WARDROBE_CATEGORY_DEFS.map((def) => {
    let count = countFairStyleSpaceWardrobeItems(items, def.id)
    if (hasServerCounts) {
      if (def.id === 'all') count = totalItems ?? count
      if (def.id === 'tops') count = typeTotal(OUTFIT_TYPE_ID.top, OUTFIT_TYPE_ID.fullbody)
      if (def.id === 'bottoms') count = typeTotal(OUTFIT_TYPE_ID.bottom)
      if (def.id === 'shoes') count = typeTotal(OUTFIT_TYPE_ID.shoes)
      if (def.id === 'outerwear') count = typeTotal(OUTFIT_TYPE_ID.outwear)
    }
    return { id: def.id, labelKey: def.labelKey, count }
  })
}

export function formatFairStyleSpaceWardrobeChipLabel(
  categoryLabel: string,
  count: number,
): string {
  return `${categoryLabel} • ${count}`
}

/** `image: ""` means the backend could not render it (§2.0) — never an error. */
export function hasFairStyleSpaceImage(src: string | null | undefined): boolean {
  return typeof src === 'string' && src.length > 0
}

/** Neutral tile drawn when the API sends `""` — same `<img>`, so the layout never shifts. */
export const FAIR_STYLE_SPACE_IMAGE_PLACEHOLDER =
  'data:image/svg+xml;utf8,'
  + encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120"><rect width="120" height="120" rx="12" fill="rgba(35,47,69,0.06)"/><path d="M60 30a8 8 0 0 0-8 8h6a2 2 0 1 1 3 1.7c-2 1.1-3 2.6-3 4.8V49h4v-3.5c0-.7.3-1.1 1-1.5a8 8 0 0 0-3-14.5zM30 84l30-24 30 24z" fill="rgba(35,47,69,0.28)"/></svg>',
  )

export function resolveFairStyleSpaceImage(src: string | null | undefined): string {
  return hasFairStyleSpaceImage(src) ? (src as string) : FAIR_STYLE_SPACE_IMAGE_PLACEHOLDER
}
